import { useState, useCallback } from 'react';
import { useTheme, useMediaQuery } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/de';
import { ChartBar, MagnifyingGlass, FileXls, ArrowRight, ArrowLeft, ArrowUp, ArrowDown, ArrowsClockwise, CaretDown, CaretUp, CaretRight, FloppyDisk, Trash } from '@phosphor-icons/react';
import { getApiUrl, authFetch } from '../config';
import { useAuth } from '../contexts/AuthContext';
import { formatDateTime } from '../utils/dateUtils';
import SubPageHeader from '../components/SubPageHeader';

dayjs.locale('de');

// ── Types ──

interface Column {
  key: string;
  label: string;
  type: 'text' | 'decimal' | 'integer';
}

interface QueryResult {
  columns: Column[];
  data: any[];
  total: number;
  executionTimeMs: number;
}

interface RebuildStatus {
  lastRebuild: {
    started_at: string;
    finished_at: string | null;
    duration_seconds: number | null;
    rows_total: number;
    status: 'running' | 'completed' | 'failed';
    error_message: string | null;
  } | null;
}

// ── Constants ──

// Kaskaden-Reihenfolge: jeder Schritt filtert den naechsten
const STEPS_CALLBACKS = [
  { key: 'lotterien', label: 'Lotterien', dataKey: 'lotteries', valueField: 'nummer', labelField: 'nummer' },
  { key: 'callcenters', label: 'Callcenter', dataKey: 'callcenters', valueField: 'name_kurz', labelField: 'name_kurz' },
  { key: 'campaigns', label: 'Kampagnen', dataKey: 'campaigns', valueField: 'kurz', labelField: 'kurz' },
  { key: 'werbewege', label: 'Werbewege', dataKey: 'werbewege', valueField: 'kurz', labelField: 'kurz' },
  { key: 'vermittler', label: 'Vermittler / Lief.', dataKey: 'vermittler', valueField: 'kurz', labelField: 'kurz' },
  { key: 'werbetraeger', label: 'Werbetr. / Liste', dataKey: 'werbetraeger', valueField: 'kurz', labelField: 'kurz' },
];

// MUB: volle Kaskade ueber actions-Join (wie Callbacks)
const STEPS_MUB = [
  { key: 'lotterien', label: 'Lotterien', dataKey: 'lotteries', valueField: 'nummer', labelField: 'nummer' },
  { key: 'callcenters', label: 'Callcenter', dataKey: 'callcenters', valueField: 'name_kurz', labelField: 'name_kurz' },
  { key: 'campaigns', label: 'Kampagnen', dataKey: 'campaigns', valueField: 'kurz', labelField: 'kurz' },
  { key: 'werbewege', label: 'Werbewege', dataKey: 'werbewege', valueField: 'kurz', labelField: 'kurz' },
  { key: 'vermittler', label: 'Vermittler / Lief.', dataKey: 'vermittler', valueField: 'kurz', labelField: 'kurz' },
  { key: 'werbetraeger', label: 'Werbetr. / Liste', dataKey: 'werbetraeger', valueField: 'kurz', labelField: 'kurz' },
];

const GRUPPIERUNGEN_MUB = [
  { key: 'lotterie', label: 'Lotterie' },
  { key: 'klasse', label: 'Klasse' },
  { key: 'mandant', label: 'Mandant' },
  { key: 'callcenter', label: 'Callcenter' },
  { key: 'kampagne', label: 'Kampagne' },
  { key: 'werbeweg', label: 'Werbeweg' },
  { key: 'vermittler', label: 'Verm./Lief.' },
  { key: 'werbetraeger', label: 'Werbetr./Liste' },
  { key: 'monat', label: 'Monat' },
  { key: 'woche', label: 'Woche' },
  { key: 'tag', label: 'Tag' },
];

const GRUPPIERUNGEN_CALLBACKS = [
  { key: 'lotterie', label: 'Lotterie' },
  { key: 'callcenter', label: 'Callcenter' },
  { key: 'kampagne', label: 'Kampagne' },
  { key: 'werbeweg', label: 'Werbeweg' },
  { key: 'vermittler', label: 'Verm./Lief.' },
  { key: 'werbetraeger', label: 'Werbetr./Liste' },
  { key: 'monat', label: 'Monat' },
  { key: 'woche', label: 'Woche' },
  { key: 'tag', label: 'Tag' },
];

// Faktura (ESW): Lotterien + Dimensionen als Kaskaden-Filter (#36)
const STEPS_FAKTURA = [
  { key: 'lotterien', label: 'Lotterien', dataKey: 'lotteries', valueField: 'nummer', labelField: 'nummer' },
  { key: 'callcenters', label: 'Callcenter', dataKey: 'callcenters', valueField: 'name_kurz', labelField: 'name_kurz' },
  { key: 'campaigns', label: 'Kampagnen', dataKey: 'campaigns', valueField: 'kurz', labelField: 'kurz' },
  { key: 'werbewege', label: 'Werbewege', dataKey: 'werbewege', valueField: 'kurz', labelField: 'kurz' },
  { key: 'vermittler', label: 'Vermittler / Lief.', dataKey: 'vermittler', valueField: 'kurz', labelField: 'kurz' },
  { key: 'werbetraeger', label: 'Werbetr. / Liste', dataKey: 'werbetraeger', valueField: 'kurz', labelField: 'kurz' },
];

const GRUPPIERUNGEN_FAKTURA = [
  { key: 'lotterie', label: 'Lotterie' },
  { key: 'klasse', label: 'Klasse' },
  { key: 'mandant', label: 'Mandant' },
  { key: 'callcenter', label: 'Callcenter' },
  { key: 'kampagne', label: 'Kampagne' },
  { key: 'werbeweg', label: 'Werbeweg' },
  { key: 'vermittler', label: 'Verm./Lief.' },
  { key: 'werbetraeger', label: 'Werbetr./Liste' },
  { key: 'faktur_art', label: 'Faktur-Art' },
  { key: 'losart', label: 'Losart' },
  { key: 'status', label: 'Status' },
  { key: 'wkz', label: 'WKZ' },
  { key: 'wkz_bez', label: 'WKZ Bezeichnung' },
  { key: 'monat', label: 'Klasse-Monat' },
  { key: 'werbe_monat', label: 'Werbe-Monat' },
  { key: 'woche', label: 'Woche' },
  { key: 'tag', label: 'Tag' },
];

// ── Presets ──

interface FilterPreset {
  name: string;
  mandant: 'NKL' | 'SKL' | '';
  datumVon: string | null;
  datumBis: string | null;
  selections: Record<string, string[]>;
  standardLose: boolean;
  folgeLose: boolean;
  nurNeukunden: boolean;
  bezahlt: boolean;
  unbezahlt: boolean;
  nur1Klasse: boolean;
  gewaehlt: string[];
  showLose: boolean;
  showJoker: boolean;
  showKunden: boolean;
  showGruppenSummen: boolean;
}

const PRESETS_KEY = 'auswertungen_presets';

function loadPresets(): FilterPreset[] {
  try {
    return JSON.parse(localStorage.getItem(PRESETS_KEY) || '[]');
  } catch { return []; }
}

function savePresetsToStorage(presets: FilterPreset[]) {
  localStorage.setItem(PRESETS_KEY, JSON.stringify(presets));
}

// ── Styles ──

const cardStyle: React.CSSProperties = {
  backgroundColor: 'white',
  padding: '1.25rem',
  borderRadius: '8px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  border: '1px solid #e5e7eb',
  marginBottom: '1rem',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '0.8rem',
  fontWeight: 600,
  color: '#374151',
  marginBottom: '0.25rem',
};

const inputStyle: React.CSSProperties = {
  padding: '0.4rem 0.5rem',
  border: '1px solid #d1d5db',
  borderRadius: '4px',
  fontSize: '0.85rem',
  width: '100%',
  boxSizing: 'border-box' as const,
};

const listBoxStyle: React.CSSProperties = {
  ...inputStyle,
  height: '150px',
  overflowY: 'auto' as const,
};

const btnPrimary: React.CSSProperties = {
  padding: '0.5rem 1.25rem',
  backgroundColor: '#2563eb',
  color: 'white',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
  fontSize: '0.9rem',
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  gap: '0.4rem',
};

const btnSecondary: React.CSSProperties = {
  padding: '0.5rem 1.25rem',
  backgroundColor: '#16a34a',
  color: 'white',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
  fontSize: '0.9rem',
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  gap: '0.4rem',
};

const btnSmall: React.CSSProperties = {
  padding: '0.25rem 0.5rem',
  backgroundColor: '#f3f4f6',
  border: '1px solid #d1d5db',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '0.8rem',
  display: 'flex',
  alignItems: 'center',
};

const stepCardStyle: React.CSSProperties = {
  ...cardStyle,
  borderLeft: '3px solid #2563eb',
};

const stepCardInactiveStyle: React.CSSProperties = {
  ...cardStyle,
  borderLeft: '3px solid #d1d5db',
  opacity: 0.5,
  pointerEvents: 'none' as const,
};

// ── Component ──

export default function Auswertungen() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isAdmin } = useAuth();

  // Datenquelle: nur noch Faktura (ESW)
  const datenquelle = 'faktura' as const;
  const activeSteps = STEPS_FAKTURA;

  // Step 0: Firma + Zeitraum
  const [mandant, setMandant] = useState<'NKL' | 'SKL' | ''>('NKL');
  const [datumVon, setDatumVon] = useState<Dayjs | null>(dayjs('2025-01-01'));
  const [datumBis, setDatumBis] = useState<Dayjs | null>(dayjs('2027-01-01'));

  // Kaskade: welcher Schritt ist aktiv (0 = noch nicht geladen, 1 = Lotterien sichtbar, etc.)
  const [activeStep, setActiveStep] = useState(0);
  const [loadingStep, setLoadingStep] = useState<number | null>(null);

  // Verfuegbare Optionen pro Stufe (vom Backend gefiltert)
  const [options, setOptions] = useState<Record<string, any[]>>({});

  // Ausgewaehlte Werte pro Stufe
  const [selections, setSelections] = useState<Record<string, string[]>>({
    lotterien: [],
    callcenters: [],
    campaigns: [],
    werbewege: [],
    vermittler: [],
    werbetraeger: [],
  });

  // Weitere Selektionen
  const [standardLose, setStandardLose] = useState(true);
  const [folgeLose, setFolgeLose] = useState(true);
  const [nurNeukunden, setNurNeukunden] = useState(true);
  const [bezahlt, setBezahlt] = useState(true);
  const [unbezahlt, setUnbezahlt] = useState(false);
  const [nur1Klasse, setNur1Klasse] = useState(false);
  const [lotterieBeginn, setLotterieBeginn] = useState<'alle' | 'vor' | 'nach'>('alle');
  const [letzterEinzug, setLetzterEinzug] = useState<Dayjs | null>(null);
  const [lotterieBeginnDatum, setLotterieBeginnDatum] = useState<Dayjs | null>(null);
  const [nachKlassen, setNachKlassen] = useState<number[]>([]);
  const [nachFolgeLotterie, setNachFolgeLotterie] = useState(false);

  // Gruppierung (dynamisch je nach Datenquelle)
  const gruppierungen = GRUPPIERUNGEN_FAKTURA;
  const [verfuegbar, setVerfuegbar] = useState<string[]>(GRUPPIERUNGEN_MUB.map(g => g.key));
  const [gewaehlt, setGewaehlt] = useState<string[]>([]);
  const [verfuegbarSel, setVerfuegbarSel] = useState<string>('');
  const [gewaehltSel, setGewaehltSel] = useState<string>('');

  // Ausgaben
  const [showLose, setShowLose] = useState(true);
  const [showJoker, setShowJoker] = useState(false);
  const [showKunden, setShowKunden] = useState(false);
  const [showCallbacks, setShowCallbacks] = useState(false);
  const [showCallbacksUnique, setShowCallbacksUnique] = useState(false);
  const [showCallbacksVersand, setShowCallbacksVersand] = useState(false);
  const [showGruppenSummen, setShowGruppenSummen] = useState(true);

  // Results
  const [result, setResult] = useState<QueryResult | null>(null);
  const [reportResult, setReportResult] = useState<{ type: 'aktionen' | 'callcenter'; data: any } | null>(null);
  const [reportExpanded, setReportExpanded] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [fakturaBuilding, setFakturaBuilding] = useState(false);
  const [hasFactTable, setHasFactTable] = useState(false);

  // Sort
  const [sortCol, setSortCol] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [error, setError] = useState('');

  // Presets
  const [presets, setPresets] = useState<FilterPreset[]>(() => loadPresets());
  const [selectedPreset, setSelectedPreset] = useState('');
  const [presetName, setPresetName] = useState('');

  const currentToPreset = (name: string): FilterPreset => ({
    name, mandant,
    datumVon: datumVon?.format('YYYY-MM-DD') || null,
    datumBis: datumBis?.format('YYYY-MM-DD') || null,
    selections, standardLose, folgeLose, nurNeukunden, bezahlt, unbezahlt, nur1Klasse,
    gewaehlt, showLose, showJoker, showKunden, showGruppenSummen,
  });

  const applyPresetState = (preset: FilterPreset) => {
    setMandant(preset.mandant);
    setDatumVon(preset.datumVon ? dayjs(preset.datumVon) : null);
    setDatumBis(preset.datumBis ? dayjs(preset.datumBis) : null);
    setSelections(preset.selections);
    setStandardLose(preset.standardLose);
    setFolgeLose(preset.folgeLose);
    setNurNeukunden(preset.nurNeukunden);
    setBezahlt(preset.bezahlt);
    setUnbezahlt(preset.unbezahlt);
    setNur1Klasse(preset.nur1Klasse);
    setShowLose(preset.showLose);
    setShowJoker(preset.showJoker);
    setShowKunden(preset.showKunden);
    setShowGruppenSummen(preset.showGruppenSummen);
    setGewaehlt(preset.gewaehlt);
    setVerfuegbar(GRUPPIERUNGEN_FAKTURA.map(g => g.key).filter(k => !preset.gewaehlt.includes(k)));
  };

  const loadPresetAndRun = async (preset: FilterPreset) => {
    applyPresetState(preset);
    setResult(null);
    setError('');
    setLoading(true);

    const pMandant = preset.mandant === 'NKL' ? 'BOH' : preset.mandant === 'SKL' ? 'BOK' : undefined;

    try {
      // Kaskade laden — nur Stufen mit Selektion + erste Stufe (Lotterien)
      for (let i = 0; i < STEPS_FAKTURA.length; i++) {
        const step = STEPS_FAKTURA[i];
        const hasSelection = preset.selections[step.key]?.length > 0;
        if (i > 0 && !hasSelection) {
          setActiveStep(i + 1);
          continue;
        }
        const body: Record<string, any> = {};
        if (pMandant) body.mandant = pMandant;
        if (preset.datumVon) body.datum_von = preset.datumVon;
        if (preset.datumBis) body.datum_bis = preset.datumBis;
        for (let j = 0; j < i; j++) {
          const s = STEPS_FAKTURA[j];
          if (preset.selections[s.key]?.length > 0) body[s.key] = preset.selections[s.key];
        }
        const res = await authFetch(getApiUrl('/auswertung/filter-options-faktura'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (res.ok) {
          const data = await res.json();
          setOptions(prev => ({ ...prev, [step.dataKey]: data[step.dataKey] || [] }));
          if (data.hasFactTable !== undefined) setHasFactTable(data.hasFactTable);
        }
        setActiveStep(i + 1);
      }

      // Query ausfuehren
      const metrics: string[] = [];
      if (preset.showLose) metrics.push('lose_gs');
      if (preset.showJoker) metrics.push('joker');
      if (preset.showKunden) metrics.push('kunden');
      if (metrics.length === 0) metrics.push('lose_gs');

      const filters: Record<string, any> = {
        mandant: pMandant,
        datum_von: preset.datumVon || undefined,
        datum_bis: preset.datumBis || undefined,
        lotterien: preset.selections.lotterien?.length > 0 ? preset.selections.lotterien : undefined,
        standard_lose: preset.standardLose,
        folge_lose: preset.folgeLose,
        nur_neukunden: preset.nurNeukunden,
      };
      if (preset.selections.callcenters?.length > 0) filters.callcenters = preset.selections.callcenters;
      if (preset.selections.campaigns?.length > 0) filters.campaigns = preset.selections.campaigns;
      if (preset.selections.werbewege?.length > 0) filters.werbewege = preset.selections.werbewege;
      if (preset.selections.vermittler?.length > 0) filters.vermittler = preset.selections.vermittler;
      if (preset.selections.werbetraeger?.length > 0) filters.werbetraeger = preset.selections.werbetraeger;

      const fksts: number[] = [];
      if (preset.bezahlt) fksts.push(3);
      if (preset.unbezahlt) fksts.push(0, 1, 2);
      if (fksts.length > 0 && fksts.length < 4) filters.fksts = fksts;
      const fklart: string[] = [];
      if (preset.standardLose) fklart.push('S');
      if (preset.folgeLose) fklart.push('F');
      if (fklart.length > 0) filters.fklart = fklart;
      filters.fkfart = [100];
      if (preset.nur1Klasse) filters.klassen = ['1'];

      const qRes = await authFetch(getApiUrl('/auswertung/query-faktura'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filters, groupBy: preset.gewaehlt, metrics, showGroupSums: preset.showGruppenSummen }),
      });
      if (!qRes.ok) {
        const errData = await qRes.json();
        throw new Error(errData.error || 'Abfrage fehlgeschlagen');
      }
      setResult(await qRes.json());
    } catch (err: any) {
      setError(err.message || 'Preset-Abfrage fehlgeschlagen');
    }
    setLoading(false);
  };

  const getUniqueName = (base: string): string => {
    const names = new Set(presets.map(p => p.name));
    if (!names.has(base)) return base;
    let i = 2;
    while (names.has(`${base} ${i}`)) i++;
    return `${base} ${i}`;
  };

  const handleSavePreset = () => {
    const base = presetName.trim();
    if (!base) return;
    const name = getUniqueName(base);
    const updated = [...presets, currentToPreset(name)];
    updated.sort((a, b) => a.name.localeCompare(b.name));
    setPresets(updated);
    savePresetsToStorage(updated);
    setSelectedPreset(name);
    setPresetName('');
  };

  const handleDeletePreset = () => {
    if (!selectedPreset) return;
    const updated = presets.filter(p => p.name !== selectedPreset);
    setPresets(updated);
    savePresetsToStorage(updated);
    setSelectedPreset('');
  };

  const handleLoadPreset = (name: string) => {
    setSelectedPreset(name);
    if (!name) return;
    const preset = presets.find(p => p.name === name);
    if (preset) loadPresetAndRun(preset);
  };

  // Admin rebuild
  const [showRebuild, setShowRebuild] = useState(false);
  const [rebuildStatus, setRebuildStatus] = useState<RebuildStatus | null>(null);
  const [rebuilding, setRebuilding] = useState(false);
  const [wpoolTable, setWpoolTable] = useState('wpool');

  // ── Kaskadierte Filter laden ──

  const buildFilterBody = useCallback((upToStepIndex: number) => {
    const body: Record<string, any> = {};
    if (mandant === 'NKL') body.mandant = 'BOH';
    if (mandant === 'SKL') body.mandant = 'BOK';
    if (datumVon) body.datum_von = datumVon.format('YYYY-MM-DD');
    if (datumBis) body.datum_bis = datumBis.format('YYYY-MM-DD');

    // Alle Selektionen BIS zur angegebenen Stufe mitgeben
    for (let i = 0; i < upToStepIndex && i < activeSteps.length; i++) {
      const step = activeSteps[i];
      if (selections[step.key]?.length > 0) {
        body[step.key] = selections[step.key];
      }
    }
    return body;
  }, [mandant, datumVon, datumBis, selections]);

  const loadStep = async (stepIndex: number) => {
    setLoadingStep(stepIndex);
    setError('');
    try {
      const body = buildFilterBody(stepIndex);
      const endpoint = '/auswertung/filter-options-faktura';
      const res = await authFetch(getApiUrl(endpoint), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Fehler beim Laden');
      const data = await res.json();

      // Optionen fuer diese Stufe setzen
      const step = activeSteps[stepIndex];
      setOptions(prev => ({ ...prev, [step.dataKey]: data[step.dataKey] || [] }));

      // Faktura: hasFactTable-Status merken (#36)
      if (data.hasFactTable !== undefined) setHasFactTable(data.hasFactTable);

      // Diese Stufe aktivieren (immer stepIndex + 1, da startLaden vorher alles reset)
      setActiveStep(stepIndex + 1);

      // Selektion fuer diese und alle folgenden Stufen zuruecksetzen
      const newSelections = { ...selections };
      for (let i = stepIndex; i < activeSteps.length; i++) {
        newSelections[activeSteps[i].key] = [];
      }
      setSelections(newSelections);
    } catch {
      setError('Selektionswerte konnten nicht geladen werden');
    }
    setLoadingStep(null);
  };

  // Initiales Laden (Schritt 1: Lotterien)
  const startLaden = () => {
    setActiveStep(0);
    setResult(null);
    setOptions({});
    setSelections({
      lotterien: [], callcenters: [], campaigns: [],
      werbewege: [], vermittler: [], werbetraeger: [],
    });
    // Gruppierung zuruecksetzen auf aktuelle Datenquelle
    const grp = GRUPPIERUNGEN_FAKTURA;
    setVerfuegbar(grp.map(g => g.key));
    setGewaehlt([]);
    loadStep(0);
  };

  // Wenn Selektion in einer Stufe geaendert wird → naechste Stufe laden
  const onSelectionChange = (stepIndex: number, values: string[]) => {
    const step = activeSteps[stepIndex];
    const newSelections = { ...selections, [step.key]: values };
    for (let i = stepIndex + 1; i < activeSteps.length; i++) {
      newSelections[activeSteps[i].key] = [];
    }
    setSelections(newSelections);
    setActiveStep(stepIndex + 1);
  };

  const loadNextStep = (stepIndex: number) => {
    if (stepIndex + 1 < activeSteps.length) {
      loadStep(stepIndex + 1);
    }
  };

  // ── Query / Export ──

  const buildRequestBody = useCallback(() => {
    const metrics: string[] = [];
    if (showLose) metrics.push('lose_gs');    // Grundspiel-Lose (fkfart=100)
    if (showJoker) metrics.push('joker');    // Joker-Lose (fkfart!=100)
    if (showKunden) metrics.push('kunden');
    if (metrics.length === 0) metrics.push('lose_gs');

    const filters: Record<string, any> = {
      mandant: mandant === 'NKL' ? 'BOH' : mandant === 'SKL' ? 'BOK' : undefined,
      datum_von: datumVon?.format('YYYY-MM-DD') || undefined,
      datum_bis: datumBis?.format('YYYY-MM-DD') || undefined,
      lotterien: selections.lotterien.length > 0 ? selections.lotterien : undefined,
      standard_lose: standardLose,
      folge_lose: folgeLose,
      nur_neukunden: nurNeukunden,
    };

    // Kaskaden-Filter fuer beide Modi
    if (selections.callcenters.length > 0) filters.callcenters = selections.callcenters;
    if (selections.campaigns.length > 0) filters.campaigns = selections.campaigns;
    if (selections.werbewege.length > 0) filters.werbewege = selections.werbewege;
    if (selections.vermittler.length > 0) filters.vermittler = selections.vermittler;
    if (selections.werbetraeger.length > 0) filters.werbetraeger = selections.werbetraeger;

    // Faktura: Status- und Losart-Filter
    const fksts: number[] = [];
    if (bezahlt) fksts.push(3);
    if (unbezahlt) fksts.push(0, 1, 2);
    if (fksts.length > 0 && fksts.length < 4) filters.fksts = fksts;
    const fklart: string[] = [];
    if (standardLose) fklart.push('S');
    if (folgeLose) fklart.push('F');
    if (fklart.length > 0) filters.fklart = fklart;
    filters.fkfart = [100]; // TODO: UI-Option für Joker (120, 140)
    if (nur1Klasse) filters.klassen = ['1'];

    return {
      filters,
      groupBy: gewaehlt,
      metrics,
      showGroupSums: showGruppenSummen,
    };
  }, [mandant, datumVon, datumBis, selections, standardLose, folgeLose, nurNeukunden, nur1Klasse, bezahlt, unbezahlt, datenquelle, gewaehlt, showLose, showJoker, showKunden, showCallbacks, showCallbacksUnique, showCallbacksVersand, showGruppenSummen]);

  const zahlenEinlesen = async () => {
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const queryEndpoint = '/auswertung/query-faktura';
      const res = await authFetch(getApiUrl(queryEndpoint), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildRequestBody()),
      });
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || 'Abfrage fehlgeschlagen');
      }
      setResult(await res.json());
    } catch (err: any) {
      setError(err.message || 'Abfrage fehlgeschlagen');
    }
    setLoading(false);
  };

  const excelExport = async () => {
    setExporting(true);
    setError('');
    try {
      const exportEndpoint = '/auswertung/export-faktura';
      const res = await authFetch(getApiUrl(exportEndpoint), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildRequestBody()),
      });
      if (!res.ok) throw new Error('Export fehlgeschlagen');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Auswertung_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.message || 'Export fehlgeschlagen');
    }
    setExporting(false);
  };

  // ── Report-Funktionen (Aktionen / Callcenter) ──

  const buildReportBody = () => {
    const mdt = mandant === 'NKL' ? 'BOH' : mandant === 'SKL' ? 'BOK' : '';
    const body: Record<string, any> = {
      mandant: mdt,
      lotterie: selections.lotterien[0] || '',
    };
    if (selections.callcenters.length > 0) body.callcenters = selections.callcenters;
    if (selections.campaigns.length > 0) body.campaigns = selections.campaigns;
    if (selections.werbewege.length > 0) body.werbewege = selections.werbewege;
    if (selections.vermittler.length > 0) body.vermittler = selections.vermittler;
    if (selections.werbetraeger.length > 0) body.werbetraeger = selections.werbetraeger;
    return body;
  };

  const fetchReport = async (type: 'aktionen' | 'callcenter') => {
    if (!mandant || !selections.lotterien?.length) {
      setError('Bitte Mandant und eine Lotterie waehlen');
      return;
    }
    setLoading(true);
    setError('');
    setResult(null);
    setReportResult(null);
    setReportExpanded(new Set());
    try {
      const endpoint = type === 'aktionen' ? '/auswertung/report-aktionen' : '/auswertung/report-callcenter';
      const res = await authFetch(getApiUrl(endpoint), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildReportBody()),
      });
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || 'Report fehlgeschlagen');
      }
      setReportResult({ type, data: await res.json() });
    } catch (err: any) {
      setError(err.message || 'Report fehlgeschlagen');
    }
    setLoading(false);
  };

  const exportReport = async (type: 'aktionen' | 'callcenter') => {
    if (!mandant || !selections.lotterien?.length) return;
    setExporting(true);
    setError('');
    try {
      const endpoint = type === 'aktionen' ? '/auswertung/export-aktionen' : '/auswertung/export-callcenter';
      const res = await authFetch(getApiUrl(endpoint), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildReportBody()),
      });
      if (!res.ok) throw new Error('Export fehlgeschlagen');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const label = mandant === 'NKL' ? 'NKL' : 'SKL';
      a.download = `${type === 'aktionen' ? 'Aktionen' : 'Callcenter'}_Report_${label}_${selections.lotterien[0]}_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.message || 'Export fehlgeschlagen');
    }
    setExporting(false);
  };

  const toggleReportExpand = useCallback((key: string) => {
    setReportExpanded(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  const expandAllReport = useCallback(() => {
    if (!reportResult) return;
    const keys = new Set<string>();
    if (reportResult.type === 'aktionen') {
      reportResult.data.aktionen?.forEach((a: any) => {
        keys.add(`akt:${a.key_aktion}`);
        a.callcenters?.forEach((cc: any) => keys.add(`akt:${a.key_aktion}:cc:${cc.name}`));
      });
    } else {
      reportResult.data.callcenters?.forEach((cc: any) => {
        keys.add(`cc:${cc.name}`);
        cc.aktionen?.forEach((a: any) => keys.add(`cc:${cc.name}:akt:${a.key_aktion}`));
      });
    }
    setReportExpanded(keys);
  }, [reportResult]);

  // Faktura-Rebuild (#36)
  const rebuildFaktura = async () => {
    if (!mandant || !selections.lotterien?.length) return;
    setFakturaBuilding(true);
    try {
      await authFetch(getApiUrl('/auswertung/rebuild-faktura'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mandant, lotterie: selections.lotterien[0] }),
      });
      // Poll bis fertig
      const pollInterval = setInterval(async () => {
        try {
          const resp = await authFetch(getApiUrl('/auswertung/faktura-tables'));
          const tables = await resp.json();
          const tableName = `fact_faktura_${mandant.toLowerCase()}_${selections.lotterien[0]}`;
          const t = tables.find((t: any) => t.table_name === tableName);
          if (!t?.lastBuild || t.lastBuild.status !== 'running') {
            clearInterval(pollInterval);
            setFakturaBuilding(false);
            setHasFactTable(true);
          }
        } catch { /* ignore poll errors */ }
      }, 2000);
    } catch {
      setFakturaBuilding(false);
    }
  };

  // Gruppierung
  const moveRight = () => {
    if (verfuegbarSel && verfuegbar.includes(verfuegbarSel)) {
      setVerfuegbar(verfuegbar.filter(k => k !== verfuegbarSel));
      setGewaehlt([...gewaehlt, verfuegbarSel]);
      setVerfuegbarSel('');
    }
  };
  const moveLeft = () => {
    const sel = gewaehltSel || (gewaehlt.length === 1 ? gewaehlt[0] : '');
    if (sel && gewaehlt.includes(sel)) {
      setGewaehlt(gewaehlt.filter(k => k !== sel));
      setVerfuegbar([...verfuegbar, sel]);
      setGewaehltSel('');
    }
  };
  const moveUp = () => {
    const idx = gewaehlt.indexOf(gewaehltSel);
    if (idx > 0) {
      const copy = [...gewaehlt];
      [copy[idx - 1], copy[idx]] = [copy[idx], copy[idx - 1]];
      setGewaehlt(copy);
    }
  };
  const moveDown = () => {
    const idx = gewaehlt.indexOf(gewaehltSel);
    if (idx >= 0 && idx < gewaehlt.length - 1) {
      const copy = [...gewaehlt];
      [copy[idx], copy[idx + 1]] = [copy[idx + 1], copy[idx]];
      setGewaehlt(copy);
    }
  };

  // Admin rebuild
  const loadStatus = async () => {
    const res = await authFetch(getApiUrl('/auswertung/status'));
    if (res.ok) setRebuildStatus(await res.json());
  };
  const runRebuild = async () => {
    if (!wpoolTable) return;
    setRebuilding(true);
    try {
      const res = await authFetch(getApiUrl('/auswertung/rebuild'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wpool_table: wpoolTable }),
      });
      if (!res.ok) {
        const errData = await res.json();
        setError(errData.error || 'Rebuild fehlgeschlagen');
        setRebuilding(false);
        return;
      }
      const poll = setInterval(async () => {
        const statusRes = await authFetch(getApiUrl('/auswertung/status'));
        if (statusRes.ok) {
          const status = await statusRes.json();
          setRebuildStatus(status);
          if (status.lastRebuild?.status !== 'running') {
            clearInterval(poll);
            setRebuilding(false);
          }
        }
      }, 2000);
    } catch {
      setError('Rebuild fehlgeschlagen');
      setRebuilding(false);
    }
  };

  // ── Helpers ──

  const formatValue = (value: any, type: string) => {
    if (value === null || value === undefined) return '';
    if (type === 'decimal') {
      return Number(value).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    if (type === 'integer') return Number(value).toLocaleString('de-DE');
    return String(value);
  };

  const isRollupRow = (row: any) => gewaehlt.some(g => row[g] === null);
  const getLabel = (key: string) => gruppierungen.find(g => g.key === key)?.label || key;

  // Sind alle Kaskaden-Schritte soweit geladen dass man auswerten kann?
  const canQuery = activeStep >= 1;

  // ── Render ──

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="de">
    <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}>
      <SubPageHeader title="Los-Daten — AdHoc Auswertungen" />
      <main style={{ maxWidth: '1400px', margin: isMobile ? '1rem auto' : '2rem auto', padding: isMobile ? '0 0.75rem' : '0 2rem' }}>

        <h1 style={{ color: '#003366', fontSize: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.25rem' }}>
          <ChartBar size={26} weight="fill" /> Los-Daten — Auswertungen
        </h1>

        {/* ── FILTER-PRESETS ── */}
        <div style={{ ...cardStyle, display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center' }}>
          <label style={{ ...labelStyle, marginBottom: 0, whiteSpace: 'nowrap' }}>Filter-Vorlage:</label>
          <select
            value={selectedPreset}
            onChange={(e) => handleLoadPreset(e.target.value)}
            style={{ ...inputStyle, width: 'auto', minWidth: '200px', flex: '0 1 auto' }}
          >
            <option value="">— Vorlage waehlen —</option>
            {presets.map(p => (
              <option key={p.name} value={p.name}>{p.name}</option>
            ))}
          </select>
          {selectedPreset && (
            <button
              onClick={handleDeletePreset}
              style={{ ...btnSmall, color: '#dc2626', borderColor: '#fca5a5' }}
              title="Vorlage loeschen"
            >
              <Trash size={14} /> Loeschen
            </button>
          )}
        </div>

        {/* ── SCHRITT 0: Firma + Zeitraum ── */}
        <div style={stepCardStyle}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem', alignItems: 'flex-end' }}>
            <div>
              <label style={labelStyle}>Firma</label>
              <div style={{ display: 'flex', gap: '0.75rem', paddingTop: '0.25rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', cursor: 'pointer', fontSize: '0.85rem' }}>
                  <input type="radio" name="mandant" checked={mandant === 'NKL'} onChange={() => setMandant('NKL')} /> NKL
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', cursor: 'pointer', fontSize: '0.85rem' }}>
                  <input type="radio" name="mandant" checked={mandant === 'SKL'} onChange={() => setMandant('SKL')} /> SKL
                </label>
              </div>
            </div>
            <div>
              <label style={labelStyle}>von</label>
              <DatePicker
                value={datumVon}
                onChange={setDatumVon}
                format="DD.MM.YYYY"
                slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
              />
            </div>
            <div>
              <label style={labelStyle}>bis</label>
              <DatePicker
                value={datumBis}
                onChange={setDatumBis}
                format="DD.MM.YYYY"
                slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
              />
            </div>
            <button
              onClick={startLaden}
              disabled={loadingStep !== null}
              style={{
                ...btnPrimary,
                backgroundColor: loadingStep !== null ? '#93c5fd' : '#2563eb',
                cursor: loadingStep !== null ? 'not-allowed' : 'pointer',
              }}
            >
              <MagnifyingGlass size={16} weight="bold" />
              {loadingStep !== null ? 'Lade...' : 'Selektionswerte auslesen'}
            </button>
            {activeStep > 0 && (
              <button
                onClick={() => { setActiveStep(0); setResult(null); setOptions({}); }}
                style={{ ...btnSmall, color: '#6b7280' }}
              >
                Reset
              </button>
            )}
          </div>
        </div>

        {/* ── KASKADIERTE FILTER-SCHRITTE ── */}
        {activeSteps.map((step, idx) => {
          const isActive = activeStep > idx;
          const isLoading = loadingStep === idx;
          const stepOptions = options[step.dataKey] || [];
          const stepSelections = selections[step.key] || [];
          const hasNext = idx + 1 < activeSteps.length;
          const nextLoaded = activeStep > idx + 1;

          if (!isActive && !isLoading) return null;
          // Stufen ohne Optionen und ohne Selektion ausblenden (z.B. beim Preset-Laden)
          if (stepOptions.length === 0 && stepSelections.length === 0 && !isLoading) return null;

          return (
            <div key={step.key} style={isActive ? stepCardStyle : stepCardInactiveStyle}>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 250px', minWidth: '200px' }}>
                  <label style={labelStyle}>
                    <span style={{ color: '#2563eb', fontWeight: 700, marginRight: '0.4rem' }}>{idx + 1}.</span>
                    {step.label}
                    <span style={{ fontWeight: 400, color: '#6b7280', marginLeft: '0.5rem' }}>
                      ({stepOptions.length} verfuegbar{stepSelections.length > 0 ? `, ${stepSelections.length} gewaehlt` : ''})
                    </span>
                  </label>
                  {isLoading ? (
                    <div style={{ padding: '1rem', color: '#6b7280', fontSize: '0.85rem' }}>Lade...</div>
                  ) : (
                    <select
                      multiple
                      style={listBoxStyle}
                      value={stepSelections}
                      onChange={e => onSelectionChange(idx, Array.from(e.target.selectedOptions, o => o.value))}
                    >
                      {stepOptions.map((item: any) => (
                        <option key={item[step.valueField]} value={String(item[step.valueField])}>
                          {item[step.labelField]}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', paddingTop: '1.5rem' }}>
                  {hasNext && !nextLoaded && (
                    <button
                      onClick={() => loadNextStep(idx)}
                      disabled={loadingStep !== null}
                      style={{
                        ...btnPrimary,
                        fontSize: '0.8rem',
                        padding: '0.4rem 1rem',
                        backgroundColor: loadingStep !== null ? '#93c5fd' : '#2563eb',
                      }}
                    >
                      <ArrowDown size={14} weight="bold" />
                      Weiter: {activeSteps[idx + 1].label}
                    </button>
                  )}
                  {stepSelections.length > 0 && (
                    <button
                      onClick={() => onSelectionChange(idx, [])}
                      style={{ ...btnSmall, fontSize: '0.75rem' }}
                    >
                      Auswahl aufheben
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {/* ── "Weiter"-Button fuer naechste nicht-geladene Stufe ── */}
        {activeStep >= 1 && (() => {
          // Finde die naechste Stufe, die keine Optionen hat (uebersprungen wurde)
          const nextUnloaded = activeSteps.findIndex((step, idx) => idx > 0 && (options[step.dataKey] || []).length === 0 && (selections[step.key] || []).length === 0);
          if (nextUnloaded > 0 && nextUnloaded < activeSteps.length) {
            return (
              <div style={{ ...cardStyle, display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <button
                  onClick={() => loadStep(nextUnloaded)}
                  disabled={loadingStep !== null}
                  style={{
                    ...btnPrimary,
                    fontSize: '0.85rem',
                    padding: '0.4rem 1rem',
                    backgroundColor: loadingStep !== null ? '#93c5fd' : '#2563eb',
                  }}
                >
                  <ArrowDown size={14} weight="bold" />
                  Weiter: {activeSteps[nextUnloaded].label}
                </button>
                <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>Filter erweitern</span>
              </div>
            );
          }
          return null;
        })()}

        {/* ── SELEKTIONEN + GRUPPIERUNG + AUSGABEN (erst wenn mindestens Lotterien geladen) ── */}
        {canQuery && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 2fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              {/* Weitere Selektionen */}
              <div style={cardStyle}>
                <h3 style={{ margin: '0 0 0.75rem 0', color: '#003366', fontSize: '0.95rem' }}>Weitere Selektionen</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', fontSize: '0.85rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', cursor: 'pointer' }}>
                    <input type="checkbox" checked={standardLose} onChange={e => setStandardLose(e.target.checked)} /> Standard-Lose
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', cursor: 'pointer' }}>
                    <input type="checkbox" checked={folgeLose} onChange={e => setFolgeLose(e.target.checked)} /> Folge-Lose
                  </label>
                  <hr style={{ border: 'none', borderTop: '1px solid #e5e7eb', margin: '0.25rem 0' }} />
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', cursor: 'pointer' }}>
                    <input type="checkbox" checked={nurNeukunden} onChange={e => setNurNeukunden(e.target.checked)} /> Nur Neukunden
                  </label>
                  <hr style={{ border: 'none', borderTop: '1px solid #e5e7eb', margin: '0.25rem 0' }} />
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', cursor: 'pointer' }}>
                    <input type="checkbox" checked={bezahlt} onChange={e => setBezahlt(e.target.checked)} /> Bezahlt
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', cursor: 'pointer' }}>
                    <input type="checkbox" checked={unbezahlt} onChange={e => setUnbezahlt(e.target.checked)} /> Unbezahlt
                  </label>
                  <hr style={{ border: 'none', borderTop: '1px solid #e5e7eb', margin: '0.25rem 0' }} />
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', cursor: 'pointer' }}>
                    <input type="checkbox" checked={nur1Klasse} onChange={e => setNur1Klasse(e.target.checked)} /> Nur 1. Klasse
                  </label>
                </div>
              </div>

              {/* Ausgabefelder und Gruppierung */}
              <div style={cardStyle}>
                <h3 style={{ margin: '0 0 0.75rem 0', color: '#003366', fontSize: '0.95rem' }}>Ausgabefelder und Gruppierung</h3>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <div style={{ flex: 1 }}>
                    <select
                      size={10}
                      style={{ ...inputStyle, height: '200px' }}
                      value={verfuegbarSel}
                      onChange={e => setVerfuegbarSel(e.target.value)}
                      onDoubleClick={moveRight}
                    >
                      {verfuegbar.map(key => (
                        <option key={key} value={key}>{getLabel(key)}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    <button onClick={moveRight} style={btnSmall} title="Hinzufuegen"><ArrowRight size={16} weight="bold" /></button>
                    <button onClick={moveLeft} style={btnSmall} title="Entfernen"><ArrowLeft size={16} weight="bold" /></button>
                  </div>
                  <div style={{ flex: 1 }}>
                    <select
                      size={10}
                      style={{ ...inputStyle, height: '200px' }}
                      value={gewaehltSel}
                      onChange={e => setGewaehltSel(e.target.value)}
                      onDoubleClick={moveLeft}
                    >
                      {gewaehlt.map(key => (
                        <option key={key} value={key}>{getLabel(key)}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    <button onClick={moveUp} style={btnSmall} title="Nach oben"><ArrowUp size={16} weight="bold" /></button>
                    <button onClick={moveDown} style={btnSmall} title="Nach unten"><ArrowDown size={16} weight="bold" /></button>
                  </div>
                </div>
              </div>

              {/* Ausgaben */}
              <div style={cardStyle}>
                <h3 style={{ margin: '0 0 0.75rem 0', color: '#003366', fontSize: '0.95rem' }}>Ausgaben</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', fontSize: '0.85rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', cursor: 'pointer' }}>
                    <input type="checkbox" checked={showLose} onChange={e => setShowLose(e.target.checked)} /> Lose
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', cursor: 'pointer' }}>
                    <input type="checkbox" checked={showJoker} onChange={e => setShowJoker(e.target.checked)} /> Faktur-Posten
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', cursor: 'pointer' }}>
                    <input type="checkbox" checked={showKunden} onChange={e => setShowKunden(e.target.checked)} /> Kunden
                  </label>
                  <hr style={{ border: 'none', borderTop: '1px solid #e5e7eb', margin: '0.25rem 0' }} />
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', cursor: 'pointer' }}>
                    <input type="checkbox" checked={showGruppenSummen} onChange={e => setShowGruppenSummen(e.target.checked)} /> Gruppen-Summen
                  </label>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '1.25rem' }}>
                  <button
                    onClick={zahlenEinlesen}
                    disabled={loading}
                    style={{
                      ...btnPrimary,
                      justifyContent: 'center',
                      backgroundColor: loading ? '#93c5fd' : '#2563eb',
                      cursor: loading ? 'not-allowed' : 'pointer',
                    }}
                  >
                    <MagnifyingGlass size={16} weight="bold" />
                    {loading ? 'Lade...' : 'Zahlen einlesen'}
                  </button>
                  <button
                    onClick={() => fetchReport('aktionen')}
                    disabled={loading}
                    style={{
                      ...btnPrimary,
                      justifyContent: 'center',
                      backgroundColor: loading ? '#93c5fd' : '#7c3aed',
                      cursor: loading ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {loading ? 'Lade...' : 'Report Aktionen'}
                  </button>
                  <button
                    onClick={() => fetchReport('callcenter')}
                    disabled={loading}
                    style={{
                      ...btnPrimary,
                      justifyContent: 'center',
                      backgroundColor: loading ? '#93c5fd' : '#7c3aed',
                      cursor: loading ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {loading ? 'Lade...' : 'Report Callcenter'}
                  </button>
                  <hr style={{ border: 'none', borderTop: '1px solid #e5e7eb', margin: '0.15rem 0' }} />
                  <button
                    onClick={excelExport}
                    disabled={exporting}
                    style={{
                      ...btnSecondary,
                      justifyContent: 'center',
                      backgroundColor: exporting ? '#86efac' : '#16a34a',
                      cursor: exporting ? 'not-allowed' : 'pointer',
                    }}
                  >
                    <FileXls size={16} weight="bold" />
                    {exporting ? 'Exportiert...' : 'Excel AdHoc'}
                  </button>
                  <button
                    onClick={() => exportReport('aktionen')}
                    disabled={exporting}
                    style={{
                      ...btnSecondary,
                      justifyContent: 'center',
                      backgroundColor: exporting ? '#86efac' : '#16a34a',
                      cursor: exporting ? 'not-allowed' : 'pointer',
                    }}
                  >
                    <FileXls size={16} weight="bold" />
                    {exporting ? 'Exportiert...' : 'Excel Aktionen'}
                  </button>
                  <button
                    onClick={() => exportReport('callcenter')}
                    disabled={exporting}
                    style={{
                      ...btnSecondary,
                      justifyContent: 'center',
                      backgroundColor: exporting ? '#86efac' : '#16a34a',
                      cursor: exporting ? 'not-allowed' : 'pointer',
                    }}
                  >
                    <FileXls size={16} weight="bold" />
                    {exporting ? 'Exportiert...' : 'Excel Callcenter'}
                  </button>
                  {mandant && selections.lotterien?.length === 1 && (
                    <button
                      onClick={rebuildFaktura}
                      disabled={fakturaBuilding}
                      title="Vorberechnete Faktura-Tabelle fuer diese Lotterie erstellen/aktualisieren"
                      style={{
                        ...btnSecondary,
                        justifyContent: 'center',
                        backgroundColor: fakturaBuilding ? '#fbbf24' : '#f59e0b',
                        color: '#1f2937',
                        cursor: fakturaBuilding ? 'not-allowed' : 'pointer',
                      }}
                    >
                      <ArrowsClockwise size={16} weight="bold" style={fakturaBuilding ? { animation: 'spin 1s linear infinite' } : undefined} />
                      {fakturaBuilding ? 'Baut Tabelle...' : hasFactTable ? 'Faktura-Tabelle aktualisieren' : 'Faktura-Tabelle erstellen'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        {/* ── Fehler ── */}
        {error && (
          <div style={{ padding: '0.75rem 1rem', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', color: '#dc2626', marginBottom: '1rem', fontSize: '0.85rem' }}>
            {error}
          </div>
        )}

        {/* ── Loading Spinner ── */}
        {loading && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '2rem', justifyContent: 'center', color: '#6b7280' }}>
            <div style={{ width: 24, height: 24, border: '3px solid #e5e7eb', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <span style={{ fontSize: '0.9rem' }}>Daten werden geladen...</span>
            <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
          </div>
        )}

        {/* ── Vorlage speichern ── */}
        {canQuery && (
          <div style={{ ...cardStyle, display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center' }}>
            <FloppyDisk size={16} style={{ color: '#6b7280' }} />
            <label style={{ ...labelStyle, marginBottom: 0, whiteSpace: 'nowrap' }}>Aktuelle Filter speichern:</label>
            <input
              type="text"
              placeholder="Name fuer neue Vorlage..."
              value={presetName}
              onChange={(e) => setPresetName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSavePreset()}
              style={{ ...inputStyle, width: 'auto', minWidth: '220px', flex: '1 1 auto', maxWidth: '350px' }}
            />
            <button
              onClick={handleSavePreset}
              disabled={!presetName.trim()}
              style={{
                ...btnSmall,
                color: presetName.trim() ? '#16a34a' : '#9ca3af',
                borderColor: presetName.trim() ? '#86efac' : '#d1d5db',
                cursor: presetName.trim() ? 'pointer' : 'not-allowed',
              }}
            >
              <FloppyDisk size={14} /> Speichern
            </button>
          </div>
        )}

        {/* ── Ergebnis-Tabelle (Access-Report-Style) ── */}
        {result && (() => {
          const textCols = result.columns.filter(c => c.type === 'text');
          const metricCols = result.columns.filter(c => c.type !== 'text');
          const allCols = [...textCols, ...metricCols];

          // 0-Zeilen ausblenden (alle Metriken == 0 oder null), aber Rollup-Zeilen behalten
          const filteredData = result.data.filter(row => {
            const rollup = showGruppenSummen && gewaehlt.some(g => row[g] === null);
            if (rollup) return true;
            return metricCols.some(col => {
              const v = Number(row[col.key]);
              return v !== 0 && !isNaN(v);
            });
          });

          // Sort
          const sortedData = sortCol ? [...filteredData].sort((a, b) => {
            const colDef = allCols.find(c => c.key === sortCol);
            const isText = colDef?.type === 'text';
            const va = a[sortCol], vb = b[sortCol];
            if (va == null && vb == null) return 0;
            if (va == null) return 1;
            if (vb == null) return -1;
            const cmp = isText
              ? String(va).localeCompare(String(vb), 'de')
              : Number(va) - Number(vb);
            return sortDir === 'asc' ? cmp : -cmp;
          }) : filteredData;

          const handleSort = (key: string) => {
            if (sortCol === key) {
              setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
            } else {
              setSortCol(key);
              setSortDir('asc');
            }
          };

          // Info fuer Header
          const mandantLabel = mandant === 'NKL' ? 'NKL' : mandant === 'SKL' ? 'SKL' : 'Alle';
          const gruppierungLabel = gewaehlt.map(g => getLabel(g)).join(' - ');
          const metrikLabel = metricCols.map(c => c.label).join(', ');

          return (
            <div style={{ ...cardStyle, padding: '1.25rem' }}>
              {/* Report-Header wie im Access */}
              <div style={{ marginBottom: '1rem', fontSize: '0.8rem', color: '#374151', lineHeight: 1.6 }}>
                <div><strong>Lotterie: {mandantLabel}{selections.lotterien.length > 0 ? ` ${selections.lotterien.join(', ')}` : ''} — {gruppierungLabel || 'Gesamt'} — {metrikLabel}</strong></div>
                <div>Zeitraum: {datumVon?.format('DD.MM.YYYY') || '–'} - {datumBis?.format('DD.MM.YYYY') || '–'}</div>
                <div style={{ color: '#6b7280' }}>Daten von: {new Date().toLocaleString('de-DE')} — {filteredData.length} Zeilen ({result.executionTimeMs}ms)</div>
              </div>

              <div style={{ overflowX: 'auto' }}>
                <table style={{
                  borderCollapse: 'collapse',
                  fontSize: '0.82rem',
                  border: '1px solid #d1d5db',
                  width: '100%',
                }}>
                  <thead>
                    <tr>
                      {allCols.map(col => {
                        // Nur rotieren wenn viele Metrik-Spalten (> 4)
                        const rotateHeaders = metricCols.length > 4;
                        const isMetric = col.type !== 'text';
                        return (
                          <th key={col.key} onClick={() => handleSort(col.key)} style={{
                            border: '1px solid #d1d5db',
                            padding: '0.4rem 0.5rem',
                            backgroundColor: sortCol === col.key ? '#e5e7eb' : '#f9fafb',
                            fontWeight: 600,
                            color: '#374151',
                            textAlign: isMetric ? 'right' : 'left',
                            whiteSpace: 'nowrap',
                            verticalAlign: 'bottom',
                            cursor: 'pointer',
                            userSelect: 'none',
                            ...(isMetric && rotateHeaders ? {
                              writingMode: 'vertical-rl' as any,
                              transform: 'rotate(180deg)',
                              height: '120px',
                              padding: '0.5rem 0.3rem',
                              textAlign: 'left' as any,
                              fontSize: '0.72rem',
                            } : {}),
                          }}>
                            {col.label}{sortCol === col.key ? (sortDir === 'asc' ? ' \u25B2' : ' \u25BC') : ''}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedData.map((row, idx) => {
                      const rollup = showGruppenSummen && isRollupRow(row);
                      const parentValues = rollup
                        ? gewaehlt.filter(g => row[g] !== null).map(g => row[g])
                        : [];
                      const isGesamt = rollup && parentValues.length === 0;
                      const sumLabel = rollup
                        ? (isGesamt ? '_Ges' : `_${parentValues.join(' ')}`)
                        : '';

                      return (
                        <tr key={idx} style={{
                          backgroundColor: isGesamt ? '#f3f4f6' : rollup ? '#fafafa' : 'white',
                          fontWeight: rollup ? 600 : 400,
                        }}>
                          {allCols.map(col => {
                            const isTextCol = col.type === 'text';
                            const val = row[col.key];

                            let display: string;
                            if (rollup && isTextCol && val === null) {
                              // Erste NULL-Textspalte bekommt das Sum-Label
                              const firstNullIdx = textCols.findIndex(tc => row[tc.key] === null);
                              display = textCols[firstNullIdx]?.key === col.key ? sumLabel : '';
                            } else {
                              display = isTextCol ? (val ?? '') : formatValue(val, col.type);
                            }

                            return (
                              <td key={col.key} style={{
                                border: '1px solid #e5e7eb',
                                padding: '0.25rem 0.5rem',
                                textAlign: isTextCol ? 'left' : 'right',
                                whiteSpace: 'nowrap',
                                fontWeight: isGesamt ? 700 : rollup ? 600 : 400,
                                color: '#111',
                                borderTop: isGesamt ? '2px solid #374151' : undefined,
                              }}>
                                {display}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                    {filteredData.length === 0 && (
                      <tr>
                        <td colSpan={allCols.length} style={{ padding: '2rem', textAlign: 'center', color: '#6b7280', border: '1px solid #e5e7eb' }}>
                          Keine Ergebnisse
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}

        {/* ── Report-Ergebnis (hierarchisch) ── */}
        {reportResult && (() => {
          const { type, data } = reportResult;
          const lotInfo = data.lotterie || {};
          const fmtDez = (v: number | null | undefined) => v == null || isNaN(Number(v)) ? '' : Number(v).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
          const fmtPct = (v: number | null | undefined) => v == null || isNaN(Number(v)) ? '' : (Number(v) * 100).toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + '%';
          const fmtDatum = (d: string) => !d || d.length < 8 ? d : `${d.substring(6,8)}.${d.substring(4,6)}.${d.substring(0,4)}`;

          const thS: React.CSSProperties = { border: '1px solid #d1d5db', padding: '0.3rem 0.5rem', backgroundColor: '#f9fafb', fontWeight: 600, fontSize: '0.75rem', textAlign: 'right', whiteSpace: 'nowrap' };
          const thT: React.CSSProperties = { ...thS, textAlign: 'left' };
          const tdS: React.CSSProperties = { border: '1px solid #e5e7eb', padding: '0.25rem 0.5rem', textAlign: 'right', fontSize: '0.78rem' };
          const tdT: React.CSSProperties = { ...tdS, textAlign: 'left' };
          const l1Bg = '#e8eef6';
          const l2Bg = '#f0f4f8';
          const l3Bg = '#fff';

          // Active filters info
          const filterInfo: string[] = [];
          if (selections.callcenters.length) filterInfo.push(`CC: ${selections.callcenters.join(', ')}`);
          if (selections.campaigns.length) filterInfo.push(`Kamp: ${selections.campaigns.join(', ')}`);
          if (selections.werbewege.length) filterInfo.push(`WW: ${selections.werbewege.join(', ')}`);

          if (type === 'aktionen') {
            const aktionen = data.aktionen || [];
            return (
              <div style={{ ...cardStyle, padding: '1.25rem' }}>
                <div style={{ marginBottom: '0.75rem', fontSize: '0.8rem', color: '#374151', lineHeight: 1.6 }}>
                  <div><strong>Aktionen-Report: {lotInfo.type} {lotInfo.nummer} — {lotInfo.name}</strong></div>
                  {filterInfo.length > 0 && <div style={{ color: '#7c3aed' }}>Filter: {filterInfo.join(' | ')}</div>}
                  <div style={{ color: '#6b7280' }}>{aktionen.length} Aktionen — {new Date().toLocaleString('de-DE')}</div>
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                    <button onClick={expandAllReport} style={{ ...btnSmall, fontSize: '0.75rem' }}>Alle aufklappen</button>
                    <button onClick={() => setReportExpanded(new Set())} style={{ ...btnSmall, fontSize: '0.75rem' }}>Alle zuklappen</button>
                  </div>
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ borderCollapse: 'collapse', fontSize: '0.78rem', border: '1px solid #d1d5db', width: '100%' }}>
                    <thead>
                      <tr>
                        <th style={thT}>Aktion</th>
                        <th style={thT}>Callcenter</th>
                        <th style={thT}>Datum</th>
                        <th style={thT}>Kampagne</th>
                        <th style={thS}>Lose best.</th>
                        <th style={thS}>Kd best.</th>
                        <th style={thS}>Kl1 bez.</th>
                        <th style={thS}>BQ Kl1</th>
                        <th style={thS}>Kl2 bez.</th>
                        <th style={thS}>BQ Kl2</th>
                        <th style={thS}>Kl3 bez.</th>
                        <th style={thS}>BQ Kl3</th>
                        <th style={thS}>Kl4 bez.</th>
                        <th style={thS}>BQ Kl4</th>
                        <th style={thS}>Kl5 bez.</th>
                        <th style={thS}>BQ Kl5</th>
                        <th style={thS}>Kl6 bez.</th>
                        <th style={thS}>BQ Kl6</th>
                        <th style={thS}>OOP</th>
                        <th style={thS}>CC-Kosten</th>
                        <th style={thS}>Ges./Los Kl1</th>
                      </tr>
                    </thead>
                    <tbody>
                      {aktionen.length === 0 && (
                        <tr><td colSpan={21} style={{ padding: '2rem', textAlign: 'center', color: '#6b7280', border: '1px solid #e5e7eb' }}>Keine Daten</td></tr>
                      )}
                      {aktionen.map((akt: any) => {
                        const l1Key = `akt:${akt.key_aktion}`;
                        const l1Exp = reportExpanded.has(l1Key);
                        return [
                          <tr key={l1Key} onClick={() => toggleReportExpand(l1Key)} style={{ backgroundColor: l1Bg, cursor: 'pointer' }}>
                            <td style={{ ...tdT, fontWeight: 700 }}>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                                {l1Exp ? <CaretDown size={12} weight="bold" /> : <CaretRight size={12} weight="bold" />}
                                {akt.aktion_name || akt.key_aktion}
                              </span>
                            </td>
                            <td style={tdT}></td>
                            <td style={tdT}></td>
                            <td style={{ ...tdT, fontWeight: 700 }}>{akt.campaign_kurz}</td>
                            <td style={{ ...tdS, fontWeight: 700 }}>{fmtDez(akt.lose_bestellt)}</td>
                            <td style={{ ...tdS, fontWeight: 700 }}>{fmtDez(akt.kunden_bestellt)}</td>
                            <td style={{ ...tdS, fontWeight: 700 }}>{fmtDez(akt.lose_kl1_bezahlt)}</td>
                            <td style={{ ...tdS, fontWeight: 700 }}>{fmtPct(akt.bezahlquote_kl1)}</td>
                            <td style={{ ...tdS, fontWeight: 700 }}>{fmtDez(akt.lose_kl2_bezahlt)}</td>
                            <td style={{ ...tdS, fontWeight: 700 }}>{fmtPct(akt.bezahlquote_kl2)}</td>
                            <td style={{ ...tdS, fontWeight: 700 }}>{fmtDez(akt.lose_kl3_bezahlt)}</td>
                            <td style={{ ...tdS, fontWeight: 700 }}>{fmtPct(akt.bezahlquote_kl3)}</td>
                            <td style={{ ...tdS, fontWeight: 700 }}>{fmtDez(akt.lose_kl4_bezahlt)}</td>
                            <td style={{ ...tdS, fontWeight: 700 }}>{fmtPct(akt.bezahlquote_kl4)}</td>
                            <td style={{ ...tdS, fontWeight: 700 }}>{fmtDez(akt.lose_kl5_bezahlt)}</td>
                            <td style={{ ...tdS, fontWeight: 700 }}>{fmtPct(akt.bezahlquote_kl5)}</td>
                            <td style={{ ...tdS, fontWeight: 700 }}>{fmtDez(akt.lose_kl6_bezahlt)}</td>
                            <td style={{ ...tdS, fontWeight: 700 }}>{fmtPct(akt.bezahlquote_kl6)}</td>
                            <td style={{ ...tdS, fontWeight: 700 }}>{fmtDez(akt.oop_kosten)}</td>
                            <td style={{ ...tdS, fontWeight: 700 }}>{fmtDez(akt.kalk_cc_kosten)}</td>
                            <td style={{ ...tdS, fontWeight: 700 }}>{fmtDez(akt.gesamtkosten_pro_bez_los_kl1)}</td>
                          </tr>,
                          ...(l1Exp ? (akt.callcenters || []).flatMap((cc: any) => {
                            const l2Key = `${l1Key}:cc:${cc.name}`;
                            const l2Exp = reportExpanded.has(l2Key);
                            return [
                              <tr key={l2Key} onClick={() => toggleReportExpand(l2Key)} style={{ backgroundColor: l2Bg, cursor: 'pointer' }}>
                                <td style={tdT}></td>
                                <td style={{ ...tdT, fontWeight: 600 }}>
                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                                    {l2Exp ? <CaretDown size={11} weight="bold" /> : <CaretRight size={11} weight="bold" />}
                                    {cc.name}
                                  </span>
                                </td>
                                <td style={tdT}></td>
                                <td style={tdT}></td>
                                <td style={{ ...tdS, fontWeight: 600 }}>{fmtDez(cc.lose_bestellt)}</td>
                                <td style={{ ...tdS, fontWeight: 600 }}>{fmtDez(cc.kunden_bestellt)}</td>
                                <td style={{ ...tdS, fontWeight: 600 }}>{fmtDez(cc.lose_kl1_bezahlt)}</td>
                                <td style={{ ...tdS, fontWeight: 600 }}>{fmtPct(cc.bezahlquote_kl1)}</td>
                                <td style={{ ...tdS, fontWeight: 600 }}>{fmtDez(cc.lose_kl2_bezahlt)}</td>
                                <td style={{ ...tdS, fontWeight: 600 }}>{fmtPct(cc.bezahlquote_kl2)}</td>
                                <td style={{ ...tdS, fontWeight: 600 }}>{fmtDez(cc.lose_kl3_bezahlt)}</td>
                                <td style={{ ...tdS, fontWeight: 600 }}>{fmtPct(cc.bezahlquote_kl3)}</td>
                                <td style={{ ...tdS, fontWeight: 600 }}>{fmtDez(cc.lose_kl4_bezahlt)}</td>
                                <td style={{ ...tdS, fontWeight: 600 }}>{fmtPct(cc.bezahlquote_kl4)}</td>
                                <td style={{ ...tdS, fontWeight: 600 }}>{fmtDez(cc.lose_kl5_bezahlt)}</td>
                                <td style={{ ...tdS, fontWeight: 600 }}>{fmtPct(cc.bezahlquote_kl5)}</td>
                                <td style={{ ...tdS, fontWeight: 600 }}>{fmtDez(cc.lose_kl6_bezahlt)}</td>
                                <td style={{ ...tdS, fontWeight: 600 }}>{fmtPct(cc.bezahlquote_kl6)}</td>
                                <td style={tdS}></td>
                                <td style={{ ...tdS, fontWeight: 600 }}>{fmtDez(cc.kalk_kosten)}</td>
                                <td style={tdS}></td>
                              </tr>,
                              ...(l2Exp ? (cc.detail || []).map((d: any, di: number) => (
                                <tr key={`${l2Key}:d:${di}`} style={{ backgroundColor: l3Bg }}>
                                  <td style={tdT}></td><td style={tdT}></td>
                                  <td style={tdT}>{fmtDatum(d.fkedat)}</td>
                                  <td style={tdT}></td>
                                  <td style={tdS}>{fmtDez(d.lose_kl1_bestellt)}</td>
                                  <td style={tdS}>{fmtDez(d.kunden_kl1_bestellt)}</td>
                                  <td style={tdS}>{fmtDez(d.lose_kl1_bezahlt)}</td>
                                  <td style={tdS}></td>
                                  <td style={tdS}>{fmtDez(d.lose_kl2_bezahlt)}</td>
                                  <td style={tdS}></td>
                                  <td style={tdS}>{fmtDez(d.lose_kl3_bezahlt)}</td>
                                  <td style={tdS}></td>
                                  <td style={tdS}>{fmtDez(d.lose_kl4_bezahlt)}</td>
                                  <td style={tdS}></td>
                                  <td style={tdS}>{fmtDez(d.lose_kl5_bezahlt)}</td>
                                  <td style={tdS}></td>
                                  <td style={tdS}>{fmtDez(d.lose_kl6_bezahlt)}</td>
                                  <td style={tdS}></td>
                                  <td style={tdS}></td><td style={tdS}></td><td style={tdS}></td>
                                </tr>
                              )) : []),
                            ];
                          }) : []),
                        ];
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          }

          // Callcenter-Report
          const callcenters = data.callcenters || [];
          return (
            <div style={{ ...cardStyle, padding: '1.25rem' }}>
              <div style={{ marginBottom: '0.75rem', fontSize: '0.8rem', color: '#374151', lineHeight: 1.6 }}>
                <div><strong>Callcenter-Report: {lotInfo.type} {lotInfo.nummer} — {lotInfo.name}</strong></div>
                {filterInfo.length > 0 && <div style={{ color: '#7c3aed' }}>Filter: {filterInfo.join(' | ')}</div>}
                <div style={{ color: '#6b7280' }}>{callcenters.length} Callcenter — {new Date().toLocaleString('de-DE')}</div>
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                  <button onClick={expandAllReport} style={{ ...btnSmall, fontSize: '0.75rem' }}>Alle aufklappen</button>
                  <button onClick={() => setReportExpanded(new Set())} style={{ ...btnSmall, fontSize: '0.75rem' }}>Alle zuklappen</button>
                </div>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', fontSize: '0.78rem', border: '1px solid #d1d5db', width: '100%' }}>
                  <thead>
                    <tr>
                      <th style={thT}>Callcenter</th>
                      <th style={thT}>Aktion</th>
                      <th style={thT}>Datum</th>
                      <th style={thT}>Kampagne</th>
                      <th style={thS}>Lose best.</th>
                      <th style={thS}>Kd best.</th>
                      <th style={thS}>Kl1 bez.</th>
                      <th style={thS}>BQ Kl1</th>
                      <th style={thS}>Kl2 bez.</th>
                      <th style={thS}>BQ Kl2</th>
                      <th style={thS}>Kl3 bez.</th>
                      <th style={thS}>BQ Kl3</th>
                      <th style={thS}>Kl4 bez.</th>
                      <th style={thS}>BQ Kl4</th>
                      <th style={thS}>Kl5 bez.</th>
                      <th style={thS}>BQ Kl5</th>
                      <th style={thS}>Kl6 bez.</th>
                      <th style={thS}>BQ Kl6</th>
                      <th style={thS}>Kalk. Kosten</th>
                      <th style={thS}>&euro;/Los Kl1</th>
                    </tr>
                  </thead>
                  <tbody>
                    {callcenters.length === 0 && (
                      <tr><td colSpan={20} style={{ padding: '2rem', textAlign: 'center', color: '#6b7280', border: '1px solid #e5e7eb' }}>Keine Daten</td></tr>
                    )}
                    {callcenters.map((cc: any) => {
                      const l1Key = `cc:${cc.name}`;
                      const l1Exp = reportExpanded.has(l1Key);
                      return [
                        <tr key={l1Key} onClick={() => toggleReportExpand(l1Key)} style={{ backgroundColor: l1Bg, cursor: 'pointer' }}>
                          <td style={{ ...tdT, fontWeight: 700 }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                              {l1Exp ? <CaretDown size={12} weight="bold" /> : <CaretRight size={12} weight="bold" />}
                              {cc.name || '(unbekannt)'}
                            </span>
                          </td>
                          <td style={tdT}></td><td style={tdT}></td><td style={tdT}></td>
                          <td style={{ ...tdS, fontWeight: 700 }}>{fmtDez(cc.lose_bestellt)}</td>
                          <td style={{ ...tdS, fontWeight: 700 }}>{fmtDez(cc.kunden_bestellt)}</td>
                          <td style={{ ...tdS, fontWeight: 700 }}>{fmtDez(cc.lose_kl1_bezahlt)}</td>
                          <td style={{ ...tdS, fontWeight: 700 }}>{fmtPct(cc.bezahlquote_kl1)}</td>
                          <td style={{ ...tdS, fontWeight: 700 }}>{fmtDez(cc.lose_kl2_bezahlt)}</td>
                          <td style={{ ...tdS, fontWeight: 700 }}>{fmtPct(cc.bezahlquote_kl2)}</td>
                          <td style={{ ...tdS, fontWeight: 700 }}>{fmtDez(cc.lose_kl3_bezahlt)}</td>
                          <td style={{ ...tdS, fontWeight: 700 }}>{fmtPct(cc.bezahlquote_kl3)}</td>
                          <td style={{ ...tdS, fontWeight: 700 }}>{fmtDez(cc.lose_kl4_bezahlt)}</td>
                          <td style={{ ...tdS, fontWeight: 700 }}>{fmtPct(cc.bezahlquote_kl4)}</td>
                          <td style={{ ...tdS, fontWeight: 700 }}>{fmtDez(cc.lose_kl5_bezahlt)}</td>
                          <td style={{ ...tdS, fontWeight: 700 }}>{fmtPct(cc.bezahlquote_kl5)}</td>
                          <td style={{ ...tdS, fontWeight: 700 }}>{fmtDez(cc.lose_kl6_bezahlt)}</td>
                          <td style={{ ...tdS, fontWeight: 700 }}>{fmtPct(cc.bezahlquote_kl6)}</td>
                          <td style={{ ...tdS, fontWeight: 700 }}>{fmtDez(cc.kalk_kosten)}</td>
                          <td style={{ ...tdS, fontWeight: 700 }}>{fmtDez(cc.kosten_pro_los_kl1)}</td>
                        </tr>,
                        ...(l1Exp ? (cc.aktionen || []).flatMap((akt: any) => {
                          const l2Key = `${l1Key}:akt:${akt.key_aktion}`;
                          const l2Exp = reportExpanded.has(l2Key);
                          return [
                            <tr key={l2Key} onClick={() => toggleReportExpand(l2Key)} style={{ backgroundColor: l2Bg, cursor: 'pointer' }}>
                              <td style={tdT}></td>
                              <td style={{ ...tdT, fontWeight: 600 }}>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                                  {l2Exp ? <CaretDown size={11} weight="bold" /> : <CaretRight size={11} weight="bold" />}
                                  {akt.aktion_name || akt.key_aktion}
                                </span>
                              </td>
                              <td style={tdT}></td>
                              <td style={{ ...tdT, fontWeight: 600 }}>{akt.campaign_kurz}</td>
                              <td style={{ ...tdS, fontWeight: 600 }}>{fmtDez(akt.lose_bestellt)}</td>
                              <td style={{ ...tdS, fontWeight: 600 }}>{fmtDez(akt.kunden_bestellt)}</td>
                              <td style={tdS}></td>
                              <td style={{ ...tdS, fontWeight: 600 }}>{fmtPct(akt.bezahlquote_kl1)}</td>
                              <td style={tdS}></td>
                              <td style={{ ...tdS, fontWeight: 600 }}>{fmtPct(akt.bezahlquote_kl2)}</td>
                              <td style={tdS}></td>
                              <td style={{ ...tdS, fontWeight: 600 }}>{fmtPct(akt.bezahlquote_kl3)}</td>
                              <td style={tdS}></td>
                              <td style={{ ...tdS, fontWeight: 600 }}>{fmtPct(akt.bezahlquote_kl4)}</td>
                              <td style={tdS}></td>
                              <td style={{ ...tdS, fontWeight: 600 }}>{fmtPct(akt.bezahlquote_kl5)}</td>
                              <td style={tdS}></td>
                              <td style={{ ...tdS, fontWeight: 600 }}>{fmtPct(akt.bezahlquote_kl6)}</td>
                              <td style={tdS}></td><td style={tdS}></td>
                            </tr>,
                            ...(l2Exp ? (akt.detail || []).map((d: any, di: number) => (
                              <tr key={`${l2Key}:d:${di}`} style={{ backgroundColor: l3Bg }}>
                                <td style={tdT}></td><td style={tdT}></td>
                                <td style={tdT}>{fmtDatum(d.fkedat)}</td>
                                <td style={tdT}></td>
                                <td style={tdS}>{fmtDez(d.lose_kl1_bestellt)}</td>
                                <td style={tdS}>{fmtDez(d.kunden_kl1_bestellt)}</td>
                                <td style={tdS}>{fmtDez(d.lose_kl1_bezahlt)}</td>
                                <td style={tdS}></td>
                                <td style={tdS}>{fmtDez(d.lose_kl2_bezahlt)}</td>
                                <td style={tdS}></td>
                                <td style={tdS}>{fmtDez(d.lose_kl3_bezahlt)}</td>
                                <td style={tdS}></td>
                                <td style={tdS}>{fmtDez(d.lose_kl4_bezahlt)}</td>
                                <td style={tdS}></td>
                                <td style={tdS}>{fmtDez(d.lose_kl5_bezahlt)}</td>
                                <td style={tdS}></td>
                                <td style={tdS}>{fmtDez(d.lose_kl6_bezahlt)}</td>
                                <td style={tdS}></td><td style={tdS}></td><td style={tdS}></td>
                              </tr>
                            )) : []),
                          ];
                        }) : []),
                      ];
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}

        {/* ── Admin: Rebuild (zugeklappt) ── */}
        {isAdmin() && (
          <div style={{ marginTop: '2rem', marginBottom: '2rem' }}>
            <button
              onClick={() => { setShowRebuild(!showRebuild); if (!showRebuild && !rebuildStatus) loadStatus(); }}
              style={{ ...btnSmall, color: '#6b7280', gap: '0.3rem', fontSize: '0.8rem' }}
            >
              {showRebuild ? <CaretUp size={14} /> : <CaretDown size={14} />}
              Admin: Data-Lake Rebuild
            </button>
            {showRebuild && (
              <div style={{ ...cardStyle, marginTop: '0.5rem', backgroundColor: '#fffbeb', borderColor: '#fde68a' }}>
                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  <div>
                    <label style={labelStyle}>WPool-Tabelle</label>
                    <input
                      type="text"
                      value={wpoolTable}
                      onChange={e => setWpoolTable(e.target.value)}
                      placeholder="z.B. wpool_20260123"
                      style={{ ...inputStyle, width: '200px' }}
                    />
                  </div>
                  <button
                    onClick={runRebuild}
                    disabled={rebuilding || !wpoolTable}
                    style={{
                      ...btnSmall,
                      backgroundColor: rebuilding ? '#fbbf24' : '#f59e0b',
                      color: '#78350f',
                      fontWeight: 600,
                      cursor: rebuilding ? 'not-allowed' : 'pointer',
                      gap: '0.3rem',
                    }}
                  >
                    <ArrowsClockwise size={14} weight="bold" />
                    {rebuilding ? 'Laeuft...' : 'Rebuild'}
                  </button>
                  <button onClick={loadStatus} style={btnSmall}>Status</button>
                </div>
                {rebuildStatus?.lastRebuild && (
                  <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: '#6b7280' }}>
                    Letzter Rebuild: {formatDateTime(rebuildStatus.lastRebuild.finished_at || rebuildStatus.lastRebuild.started_at)}
                    {rebuildStatus.lastRebuild.status === 'completed' && ` — ${rebuildStatus.lastRebuild.rows_total?.toLocaleString('de-DE')} Zeilen (${rebuildStatus.lastRebuild.duration_seconds}s)`}
                    {rebuildStatus.lastRebuild.status === 'running' && <span style={{ color: '#d97706' }}> — laeuft...</span>}
                    {rebuildStatus.lastRebuild.status === 'failed' && <span style={{ color: '#dc2626' }}> — Fehler: {rebuildStatus.lastRebuild.error_message}</span>}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
    </LocalizationProvider>
  );
}
